import { ApolloClient, gql } from '@apollo/client'
import {
  GetParentalControlsLanguagesForContentQuery,
  GetParentalControlsLanguagesForContentQueryVariables,
  GetParentalControlsLanguagesForProjectQuery,
  GetParentalControlsLanguagesForProjectQueryVariables,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { Maybe } from '@/utils/types'

export const GET_SKIP_CATEGORY_PREFERENCES_FOR_USER = gql`
  query getSkipCategoryPreferences($lang: String!) {
    user {
      id
      parentalControlPreferences(language: $lang) {
        id
        key
        displayTitle
        parentId
        isNew
        totalCount
        skipped
        skippedCount
      }
    }
  }
`

export const UPDATE_SKIP_CATEGORY_PREFERENCES_FOR_USER = gql`
  mutation updateSkipCategoryPreferences($lang: String!, $categoryId: Int!, $enabled: Boolean!) {
    user {
      updateParentalControlPreferences(language: $lang, updates: { categoryId: $categoryId, skipped: $enabled }) {
        id
        key
        displayTitle
        parentId
        skipped
        skippedCount
        totalCount
      }
    }
  }
`

export const SET_TV_PARENTAL_CONTROLS_SAVE_AND_WATCH_AT = gql`
  mutation setTvParentalControlsSaveAndWatchAt {
    setTvParentalControlsSaveAndWatchAt
  }
`

const GET_PARENTAL_CONTROLS_LANGUAGES_FOR_CONTENT = gql`
  query GetParentalControlsLanguagesForContent($id: ID!) {
    getContent(id: $id) {
      ... on Node {
        id
        __typename
      }
      ... on ContentWatchable {
        muteAllSwearsAvailability
      }
    }
    user {
      __typename
      id
      uuid
      muteAllSwearingPreferenceEnabled
    }
  }
`

const GET_PARENTAL_CONTROLS_LANGUAGES_FOR_PROJECT = gql`
  query GetParentalControlsLanguagesForProject($id: ID!) {
    getContent(id: $id) {
      ... on Node {
        id
        __typename
      }
      ... on ContentWatchable {
        muteAllSwearsAvailability
      }
    }
  }
`

interface GetParentalControlsForContent {
  client: ApolloClient
  contentId: Maybe<string>
}

export const getParentalControlsForContent = async ({
  client,
  contentId,
}: GetParentalControlsForContent): Promise<GetParentalControlsLanguagesForContentQuery | null> => {
  try {
    if (!contentId) return null

    const result = await client.query<
      GetParentalControlsLanguagesForContentQuery,
      GetParentalControlsLanguagesForContentQueryVariables
    >({
      query: GET_PARENTAL_CONTROLS_LANGUAGES_FOR_CONTENT,
      variables: { id: contentId },
      context: { requireAuth: true },
      errorPolicy: 'all',
    })

    return {
      getContent: result?.data?.getContent ?? null,
      user: result?.data?.user ?? null,
    }
  } catch (error) {
    logger().error('getParentalControlsForContent failed', { error, contentId })
    return null
  }
}

export type ParentalControlsContent = NonNullable<GetParentalControlsLanguagesForContentQuery['getContent']>

export const getParentalControlsForProject = async ({
  client,
  contentId,
}: GetParentalControlsForContent): Promise<GetParentalControlsLanguagesForProjectQuery | null> => {
  try {
    if (!contentId) return null

    const result = await client.query<
      GetParentalControlsLanguagesForProjectQuery,
      GetParentalControlsLanguagesForProjectQueryVariables
    >({
      query: GET_PARENTAL_CONTROLS_LANGUAGES_FOR_PROJECT,
      variables: { id: contentId },
      errorPolicy: 'all',
    })

    return {
      getContent: result?.data?.getContent ?? null,
    }
  } catch (error) {
    logger().error('getParentalControlsForProject failed', { error, contentId })
    return null
  }
}

export const getSkipsEnabledFromParentalControls = (
  parentalControls: ParentalControlsContent | undefined | null,
): boolean => {
  if (!parentalControls) return false
  const availability =
    parentalControls && 'muteAllSwearsAvailability' in parentalControls
      ? parentalControls.muteAllSwearsAvailability
      : null
  if (!Array.isArray(availability)) return false
  const skipsLanguages: (string | null)[] = availability.map((availableLocale: string | null | undefined) =>
    availableLocale ? availableLocale : null,
  )
  return skipsLanguages.length > 0
}
